import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Calendar,
  Input,
  InternationalPhoneInput,
  SearchSelect,
  SelectMenu,
  Text,
  UploadedFile,
} from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import DocumentUploadField from "../../../common/documentUploadField/DocumentUploadField";
import AddressForm from "../../../common/addressForm/AddressForm";
import { findCountryCode, getCountryOptions } from "../../../../common/countries/countries";
import { usePhoneValidation } from "../../../../app/hooks/usePhoneValidation";
import { useAdmin } from "../../../hooks/useAdmin";
import { useOperation } from "../../../hooks/useOperation";
import { useUpload } from "../../../../common/hooks/useUpload";
import { useEstablishment } from "../hooks/useEstablishment";
import { useEstablishmentData } from "../hooks/useEstablishmentData";
import {
  useLegalRepresentative,
  useLegalRepresentativeData,
} from "../hooks/useLegalRepresentative";
import {
  useFinancialInformation,
  useFinancialInformationData,
} from "../hooks/useFinancialInformation";
import useFormValidation from "../hooks/AddEstablishment.hook";
import Preloader from "../../../../common/preLoader/PreLoader";
import { PARTNER_TYPE, UPLOAD_RESOURCES, UPLOAD_TYPES } from "../../../common/model/enum";
import styles from "./AddEstablishementNew.module.css";

const initialAddress = {
  number: "",
  route: "",
  complement: "",
  stage: "",
  zipPostal: "",
  city: "",
};

const initialForm = {
  companyName: "",
  legalForm: "",
  ice: "",
  fiscalId: "",
  professionalTax: "",
  address: initialAddress,
  rcNumber: "",
  tribunalCity: "",
  modelJUrl: "",
  licenseNumber: "",
  licenseDate: "",
  insuranceCompany: "",
  insurancePolicy: "",
  insuranceExpiry: "",
  firstName: "",
  lastName: "",
  identityType: "",
  identityNumber: "",
  identityExpiry: "",
  professionalEmail: "",
  capitalShare: "",
  nationality: "",
  legalRepDocumentUrl: "",
  bank: "",
  accountHolder: "",
  rib: "",
};

const ACTIVITY_KEY_TO_ENUM = {
  travel: PARTNER_TYPE.TRAVEL_SERVICE,
  accommodation: PARTNER_TYPE.ACCOMMODATION_SERVICE,
};
const ACTIVITY_ENUM_TO_KEY = {
  [PARTNER_TYPE.TRAVEL_SERVICE]: "travel",
  [PARTNER_TYPE.ACCOMMODATION_SERVICE]: "accommodation",
};

const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toLocalYyyyMmDd = (value) => {
  if (!(value instanceof Date)) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const steps = [
  { key: "establishment", number: "01" },
  { key: "governance", number: "02" },
  { key: "finance", number: "03" },
];

const AddEstablishementNew = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { establishmentId: routeEstablishmentId } = useParams();
  const isEditMode = Boolean(routeEstablishmentId);

  const { organization, triggerEstablishmentsPermissions } = useAdmin();
  const { showToast } = useOperation();
  const { uploadFile } = useUpload();
  const {
    errors,
    validate,
    validateLegalRepresentative,
    validateFinancialInformation,
    clearErrors,
  } = useFormValidation();

  const [activeEstablishmentId, setActiveEstablishmentId] = useState(
    routeEstablishmentId || null
  );
  const [legalRepresentativeId, setLegalRepresentativeId] = useState(null);
  const [financialInformationId, setFinancialInformationId] = useState(null);

  const { establishment, loading: dataLoading } =
    useEstablishmentData(activeEstablishmentId);
  const {
    loading: actionLoading,
    addEstablishment,
    editEstablishment,
  } = useEstablishment(organization?.entityId);

  // true dès qu'on vient de créer le représentant légal localement dans cette
  // session (voir saveLegalRepresentativeStep) : on a déjà toutes ses données
  // dans `form`, inutile de les redemander au serveur juste après.
  const skipLegalRepFetchRef = useRef(false);
  const { legalRepresentative, loading: legalRepFetching } =
    useLegalRepresentativeData(activeEstablishmentId, legalRepresentativeId, {
      skip: skipLegalRepFetchRef.current,
    });
  const {
    addLegalRepresentative,
    updateLegalRepresentative,
    loading: legalRepSaving,
  } = useLegalRepresentative();
  // Téléphone du représentant légal : état + validation centralisés dans
  // usePhoneValidation, comme dans ProfilePage.js (indicative/number dérivés
  // d'une valeur combinée "+<indicative><number>").
  const phone = usePhoneValidation(legalRepresentative?.phoneNumber);

  // true dès qu'on vient de créer les informations financières localement
  // dans cette session (voir saveFinancialInformationStep) : on a déjà
  // toutes ses données dans `form`, inutile de les redemander au serveur.
  const skipFinancialInfoFetchRef = useRef(false);
  const { financialInformation, loading: financialInfoFetching } =
    useFinancialInformationData(activeEstablishmentId, financialInformationId, {
      skip: skipFinancialInfoFetchRef.current,
    });
  const {
    addFinancialInformation,
    updateFinancialInformation,
    loading: financialInfoSaving,
  } = useFinancialInformation();

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  // Code ISO du pays (ex. "FR") du siège social ; voir AddOrganisme pour le
  // même pattern (verrouillage/invalidation ville+code postal gérés par AddressForm).
  const [countryCode, setCountryCode] = useState("");
  // Même bibliothèque que le pays de l'adresse (react-phone-number-input +
  // Intl.DisplayNames via countries.js) : liste de codes ISO triée, noms
  // localisés selon la langue de l'interface.
  const nationalityOptions = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);
  const [activities, setActivities] = useState([]);
  const [documents, setDocuments] = useState({ modelJ: null, identity: null });

  useEffect(() => {
    if (isEditMode && establishment) {
      const registeredOffice = establishment.company?.registeredOffice;
      // Ancien enregistrement : le pays peut être un nom libre plutôt qu'un code.
      const storedCountryCode = findCountryCode(registeredOffice?.country, i18n.language);
      setForm((prev) => ({
        ...prev,
        companyName: establishment.company?.legalName || "",
        legalForm: establishment.company?.legalForm || "",
        ice: establishment.company?.ice || "",
        fiscalId: establishment.company?.taxIdentifier || "",
        professionalTax: establishment.company?.professionalTax || "",
        address: {
          number: registeredOffice?.number || "",
          route: registeredOffice?.route || "",
          complement: registeredOffice?.complement || "",
          stage: registeredOffice?.stage || "",
          zipPostal: registeredOffice?.zipPostal || "",
          city: registeredOffice?.city || "",
        },
        rcNumber: establishment.company?.commercialRegister?.number || "",
        tribunalCity:
          establishment.company?.commercialRegister?.courtCity || "",
        modelJUrl: establishment.company?.commercialRegister?.modelJ || "",
        licenseNumber: establishment.tourismAgreements?.licenseNumber || "",
        licenseDate: establishment.tourismAgreements?.issueDate || "",
        insuranceCompany:
          establishment.tourismAgreements?.professionalLiabilityInsurance
            ?.company || "",
        insurancePolicy:
          establishment.tourismAgreements?.professionalLiabilityInsurance
            ?.policyNumber || "",
        insuranceExpiry:
          establishment.tourismAgreements?.professionalLiabilityInsurance
            ?.expiryDate || "",
      }));
      setCountryCode(storedCountryCode);
      setActivities(
        (establishment.activities || [])
          .map((value) => ACTIVITY_ENUM_TO_KEY[value])
          .filter(Boolean)
      );
      setLegalRepresentativeId(establishment.legalRepresentativeId || null);
      setFinancialInformationId(establishment.financialInformationId || null);
    }
    // i18n.language volontairement absent : il ne sert qu'à retrouver un code
    // depuis un ancien nom de pays, et le relancer réinitialiserait le formulaire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [establishment, isEditMode]);

  useEffect(() => {
    if (legalRepresentative) {
      setForm((prev) => ({
        ...prev,
        firstName: legalRepresentative.firstName || "",
        lastName: legalRepresentative.lastName || "",
        identityType: legalRepresentative.identityDocumentType || "",
        identityNumber: legalRepresentative.identityNumber || "",
        identityExpiry: legalRepresentative.identityExpiryDate || "",
        professionalEmail: legalRepresentative.email || "",
        capitalShare:
          legalRepresentative.ownershipPercentage != null
            ? String(legalRepresentative.ownershipPercentage)
            : "",
        // Ancien enregistrement : la nationalité peut être un nom libre plutôt qu'un code.
        nationality: findCountryCode(legalRepresentative.nationality, i18n.language),
        legalRepDocumentUrl: legalRepresentative.supportingDocumentsId || "",
      }));
      phone.reset(legalRepresentative.phoneNumber);
    }
    // i18n.language volontairement absent, même raison que pour l'adresse ;
    // phone.reset est stable (useCallback sans deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legalRepresentative]);

  useEffect(() => {
    if (financialInformation) {
      setForm((prev) => ({
        ...prev,
        bank: financialInformation.bankName || "",
        accountHolder: financialInformation.accountHolder || "",
        rib: financialInformation.rib || "",
      }));
    }
  }, [financialInformation]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const updateSelect = (field) => (_, option) => {
    setForm((current) => ({ ...current, [field]: option?.value || "" }));
  };

  const updateDate = (field) => (date) => {
    setForm((current) => ({ ...current, [field]: toLocalYyyyMmDd(date) }));
  };

  const toggleActivity = (activity) => {
    setActivities((current) =>
      current.includes(activity)
        ? current.filter((item) => item !== activity)
        : [...current, activity]
    );
  };

  const handleDocumentPick = (field) => (files) => {
    setDocuments((current) => ({
      ...current,
      [field]: Array.isArray(files) ? files[0] || null : null,
    }));
  };

  const removeDocument = (field) => {
    setDocuments((current) => ({ ...current, [field]: null }));
  };

  const buildEstablishmentInput = (overrides = {}) => {
    // commercialRegister/tourismAgreements are optional wrapper objects whose inner
    // fields are non-null in the schema — only send them when the user actually
    // started filling that section, otherwise omit them entirely (send null).
    const hasCommercialRegister = Boolean(
      form.rcNumber || form.tribunalCity || form.modelJUrl || documents.modelJ
    );
    const hasInsurance = Boolean(
      form.insuranceCompany || form.insurancePolicy || form.insuranceExpiry
    );
    const hasTourismAgreements = Boolean(
      form.licenseNumber || form.licenseDate || hasInsurance
    );
    const hasAddress = [...Object.values(form.address), countryCode].some((value) =>
      (value || "").trim()
    );

    return {
      id: activeEstablishmentId || undefined,
      parentOrganism: organization?.entityId,
      status: "ACTIVATED",
      legalRepresentativeId: legalRepresentativeId || null,
      financialInformationId: financialInformationId || null,
      company: {
        legalName: form.companyName || null,
        legalForm: form.legalForm || null,
        ice: form.ice || null,
        commercialRegister: hasCommercialRegister
          ? {
              number: form.rcNumber || null,
              courtCity: form.tribunalCity || null,
              modelJ: form.modelJUrl || null,
            }
          : null,
        taxIdentifier: form.fiscalId || null,
        professionalTax: form.professionalTax || null,
        // Aucun champ d'adresse n'est obligatoire (schéma `address` de la
        // Partners API) : adresse vide -> null, sinon champs vides -> null.
        // country, s'il est envoyé, doit être un code ISO alpha-2 (2 lettres).
        registeredOffice: hasAddress
          ? {
              number: form.address.number || null,
              route: form.address.route || null,
              complement: form.address.complement || null,
              stage: form.address.stage || null,
              zipPostal: form.address.zipPostal || null,
              city: form.address.city || null,
              country: countryCode || null,
            }
          : null,
      },
      tourismAgreements: hasTourismAgreements
        ? {
            licenseNumber: form.licenseNumber || null,
            issueDate: form.licenseDate || null,
            professionalLiabilityInsurance: hasInsurance
              ? {
                  company: form.insuranceCompany || null,
                  policyNumber: form.insurancePolicy || null,
                  expiryDate: form.insuranceExpiry || null,
                }
              : null,
          }
        : null,
      activities: activities.map((key) => ACTIVITY_KEY_TO_ENUM[key]),
      ...overrides,
    };
  };

  // Legal representative / financial information routes are nested under an
  // establishment id — create the (possibly still empty) establishment first
  // if the user filled those sections before the "Etablissement" one.
  // company.legalName is now always required by the backend, so bounce the
  // user back to step 0 instead of firing a doomed create mutation.
  const ensureEstablishmentId = async () => {
    if (activeEstablishmentId) return activeEstablishmentId;

    const input = buildEstablishmentInput();
    if (!validate(input)) {
      setActiveStep(0);
      showToast(
        t(
          "manageEstablishment.new.errors.companyRequiredFirst",
          "Veuillez d'abord renseigner les informations obligatoires de l'entreprise."
        ),
        "error"
      );
      const blockedError = new Error("Establishment creation blocked: missing required company fields");
      blockedError.silent = true;
      throw blockedError;
    }

    const res = await addEstablishment(input);
    const newId = res.data?.MT_ADD_ESTABLISHMENT?.data?.id;
    if (!newId) throw new Error("Establishment creation failed");

    setActiveEstablishmentId(newId);
    navigate(`/admin/establishments/${newId}/edit`, { replace: true });
    return newId;
  };

  const reportError = (err) => {
    console.error("Erreur technique:", err, err?.graphQLErrors);
    if (err?.silent) return; // already reported (e.g. blocked before hitting the API)
    // Backend still rejects (e.g. 400 on an incomplete EstablishmentInput) even though the
    // form validated locally — surface its message instead of a generic one when available.
    const backendMessage = err?.graphQLErrors?.[0]?.message || err?.networkError?.result?.errors?.[0]?.message;
    showToast(
      backendMessage || t("commons.errors.technicalError", "Une erreur est survenue."),
      "error"
    );
  };

  const saveEstablishmentStep = async () => {
    const input = buildEstablishmentInput();
    if (!validate(input)) return null;

    try {
      if (documents.modelJ) {
        const res = await uploadFile(
          documents.modelJ,
          UPLOAD_RESOURCES.PARTNER,
          UPLOAD_TYPES.SUPPORTING_DOCUMENT
        );
        input.company.commercialRegister.modelJ =
          res?.fileLink || input.company.commercialRegister.modelJ;
        setForm((prev) => ({ ...prev, modelJUrl: input.company.commercialRegister.modelJ }));
        setDocuments((prev) => ({ ...prev, modelJ: null }));
      }

      let id = activeEstablishmentId;
      if (id) {
        await editEstablishment(id, input);
      } else {
        const res = await addEstablishment(input);
        id = res.data?.MT_ADD_ESTABLISHMENT?.data?.id;
        if (!id) throw new Error("Establishment creation failed");
        setActiveEstablishmentId(id);
        triggerEstablishmentsPermissions(id);
        navigate(`/admin/establishments/${id}/edit`, { replace: true });
      }

      clearErrors();
      return id;
    } catch (err) {
      reportError(err);
      return null;
    }
  };

  const saveLegalRepresentativeStep = async () => {
    if (!phone.validate()) {
      showToast(
        t(
          "manageEstablishment.new.errors.phoneInvalid",
          "Le numéro de téléphone est invalide. Corrigez-le avant d'enregistrer."
        ),
        "error"
      );
      return null;
    }

    const legalRepresentativeInput = {
      lastName: form.lastName || null,
      firstName: form.firstName || null,
      identityDocumentType: form.identityType || null,
      identityNumber: form.identityNumber || null,
      identityExpiryDate: form.identityExpiry || null,
      supportingDocumentsId: form.legalRepDocumentUrl || null,
      email: form.professionalEmail || null,
      phoneNumber: phone.phoneNumber,
      ownershipPercentage: form.capitalShare ? Number(form.capitalShare) : null,
      nationality: form.nationality || null,
    };
    if (!validateLegalRepresentative(legalRepresentativeInput)) return null;

    try {
      const id = await ensureEstablishmentId();

      let supportingDocumentsId = legalRepresentativeInput.supportingDocumentsId;
      if (documents.identity) {
        const res = await uploadFile(
          documents.identity,
          UPLOAD_RESOURCES.PARTNER,
          UPLOAD_TYPES.ID
        );
        supportingDocumentsId = res?.fileLink || supportingDocumentsId;
        legalRepresentativeInput.supportingDocumentsId = supportingDocumentsId;
      }

      if (legalRepresentativeId) {
        await updateLegalRepresentative(id, legalRepresentativeId, legalRepresentativeInput);
      } else {
        const res = await addLegalRepresentative(id, legalRepresentativeInput);
        const newLegalRepresentativeId = res.data?.MT_ADD_LEGAL_REPRESENTATIVE?.data?.id;
        if (newLegalRepresentativeId) {
          // Déjà en main via ce même formulaire : QR_GET_LEGAL_REPRESENTATIVE
          // n'a rien à apporter ici (voir skipLegalRepFetchRef).
          skipLegalRepFetchRef.current = true;
          setLegalRepresentativeId(newLegalRepresentativeId);
          await editEstablishment(
            id,
            buildEstablishmentInput({ legalRepresentativeId: newLegalRepresentativeId })
          );
        }
      }

      setForm((prev) => ({ ...prev, legalRepDocumentUrl: supportingDocumentsId || "" }));
      setDocuments((prev) => ({ ...prev, identity: null }));
      return id;
    } catch (err) {
      reportError(err);
      return null;
    }
  };

  const saveFinancialInformationStep = async () => {
    const financialInformationInput = {
      bankName: form.bank || null,
      accountHolder: form.accountHolder || null,
      rib: form.rib || null,
    };
    if (!validateFinancialInformation(financialInformationInput)) return null;

    try {
      const id = await ensureEstablishmentId();

      if (financialInformationId) {
        await updateFinancialInformation(id, financialInformationId, financialInformationInput);
      } else {
        const res = await addFinancialInformation(id, financialInformationInput);
        const newFinancialInformationId = res.data?.MT_ADD_FINANCIAL_INFORMATION?.data?.id;
        if (newFinancialInformationId) {
          // Déjà en main via ce même formulaire : QR_GET_FINANCIAL_INFORMATION
          // n'a rien à apporter ici (voir skipFinancialInfoFetchRef).
          skipFinancialInfoFetchRef.current = true;
          setFinancialInformationId(newFinancialInformationId);
          await editEstablishment(
            id,
            buildEstablishmentInput({ financialInformationId: newFinancialInformationId })
          );
        }
      }

      return id;
    } catch (err) {
      reportError(err);
      return null;
    }
  };

  const nextStep = async () => {
    let id = null;
    if (activeStep === 0) id = await saveEstablishmentStep();
    else if (activeStep === 1) id = await saveLegalRepresentativeStep();
    else id = await saveFinancialInformationStep();

    if (!id) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((step) => step + 1);
      return;
    }

    showToast(t("manageEstablishment.new.actions.submitted", "Dossier soumis"), "success");
    navigate(`/admin/establishments/${id}`);
  };

  const isSaving = actionLoading || legalRepSaving || financialInfoSaving;

  // Établissement toujours accessible. Gouvernance et Finances le deviennent
  // dès que l'établissement existe (id non nul, après sa création réussie ou
  // depuis la route d'édition) : elles dépendent toutes deux de cet id.
  const isStepUnlocked = (index) => index === 0 || Boolean(activeEstablishmentId);

  const renderInput = (field, label, type = "text", extraProps = {}, errorKey = null) => (
    <Input
      floatingLabel
      inputSize="medium"
      label={t(label)}
      name={field}
      type={type}
      value={form[field]}
      onChange={updateField(field)}
      errors={errorKey ? errors?.[errorKey] : undefined}
      {...extraProps}
    />
  );

  const renderDate = (field, label, errorKey = null, required = false) => {
    const fieldErrors = errorKey ? errors?.[errorKey] : undefined;
    return (
      <div id={`${field}Field`} style={{ display: "contents" }}>
        <div>
          <Calendar
            inputLabel={required ? `${t(label)} *` : t(label)}
            inputSize="medium"
            value={toDateSafe(form[field])}
            onChange={updateDate(field)}
          />
          {fieldErrors?.length > 0 && (
            <Text size="sm" className={styles.fieldError}>
              {fieldErrors[0]}
            </Text>
          )}
        </div>
      </div>
    );
  };

  if (isEditMode && (dataLoading || legalRepFetching || financialInfoFetching)) {
    return <Preloader />;
  }

  return (
    <section className={styles.page}>
      <div className={styles.portal}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate("/admin/establishments")}
        >
          <FontAwesomeIcon icon={faArrowLeft} size="lg" color="#666" />
          <span>Retour a la liste</span>
        </button>

        <header className={styles.header}>
          <div>
            <Text as="h1" size="xl" weight="bold" className={styles.title}>
              {t("manageEstablishment.new.add.title")}
            </Text>
            <Text as="p" size="sm" color="muted">
              {t("manageEstablishment.new.add.subtitle")}
            </Text>
          </div>
          <span className={styles.brandPill}>{t("manageEstablishment.new.brand")}</span>
        </header>

        <div className={styles.tabsNav} role="tablist" aria-label={t("manageEstablishment.new.add.stepsLabel")}>
          {steps.map((step, index) => {
            const unlocked = isStepUnlocked(index);
            return (
              <Button
                key={step.key}
                className={`${styles.tabBtn} ${activeStep === index ? styles.tabActive : ""}`}
                label={t(`manageEstablishment.new.steps.${step.key}.title`)}
                onClick={() => unlocked && setActiveStep(index)}
                disabled={!unlocked}
                iconName={
                  step.key === "establishment"
                    ? "iconBuildings"
                    : step.key === "governance"
                      ? "iconUsers"
                      : "iconCalendar"
                }
                iconPosition="left"
                variant="ghost"
                size="medium"
                role="tab"
                aria-selected={activeStep === index}
                aria-disabled={!unlocked}
              />
            );
          })}
        </div>

        <div className={styles.formContainer}>
          {activeStep === 0 && (
            <div className={styles.formPane}>
              <FormSection title={t("manageEstablishment.new.add.sections.company")}>
                <div className={styles.gridTwo}>
                  {renderInput(
                    "companyName",
                    "manageEstablishment.new.fields.companyName",
                    "text",
                    { required: true },
                    "legalName"
                  )}
                  <SelectMenu t={t}
                    name="legalForm"
                    activeSearch
                    inputSize="medium"
                    options={[
                      { id: "EI", value: "EI", text: t("manageEstablishment.new.legalForms.ei") },
                      { id: "SARL", value: "SARL", text: t("manageEstablishment.new.legalForms.sarl") },
                      { id: "SA", value: "SA", text: t("manageEstablishment.new.legalForms.sa") },
                    ]}
                    value={form.legalForm}
                    placeholder={t("manageEstablishment.new.fields.legalForm")}
                    onChange={updateSelect("legalForm")}
                  />
                  {renderInput("ice", "manageEstablishment.new.fields.ice")}
                  {renderInput("fiscalId", "manageEstablishment.new.fields.fiscalId")}
                  {renderInput("professionalTax", "manageEstablishment.new.fields.professionalTax")}
                </div>
              </FormSection>

              <FormSection title={t("manageEstablishment.new.add.sections.address")}>
                <AddressForm
                  value={{ address: form.address, countryCode }}
                  onChange={({ address, countryCode: nextCode }) => {
                    setForm((prev) => ({ ...prev, address }));
                    setCountryCode(nextCode);
                  }}
                  errors={errors}
                />
              </FormSection>

              <FormSection title={t("manageEstablishment.new.add.sections.registry")}>
                <Text size="sm" color="subtle">
                  {t(
                    "manageEstablishment.new.add.sections.registryHint",
                    "Numéro RC et ville du tribunal sont obligatoires dès que ce bloc est renseigné."
                  )}
                </Text>
                <div className={styles.gridTwo}>
                  {renderInput(
                    "rcNumber",
                    "manageEstablishment.new.fields.rcNumber",
                    "text",
                    {},
                    "rcNumber"
                  )}
                  {renderInput(
                    "tribunalCity",
                    "manageEstablishment.new.fields.tribunalCity",
                    "text",
                    {},
                    "tribunalCity"
                  )}
                  <div>
                    <Text as="label" size="sm" weight="semibold">
                      {t("manageEstablishment.new.fields.modelJ")}
                    </Text>
                    {!documents.modelJ && form.modelJUrl ? (
                      <UploadedFile t={t}
                        fileName={"Modèle J existant.pdf"}
                        isEditMode={false}
                      />
                    ) : (
                      <DocumentUploadField
                        file={documents.modelJ}
                        onUpload={handleDocumentPick("modelJ")}
                        onRemove={() => removeDocument("modelJ")}
                      />
                    )}
                  </div>
                </div>
              </FormSection>

              <FormSection title={t("manageEstablishment.new.add.sections.activities")}>
                <div className={styles.gridTwo}>
                  {renderInput(
                    "licenseNumber",
                    "manageEstablishment.new.fields.licenseNumber"
                  )}
                  {renderDate(
                    "licenseDate",
                    "manageEstablishment.new.fields.licenseDate"
                  )}
                  {renderInput(
                    "insuranceCompany",
                    "manageEstablishment.new.fields.insuranceCompany"
                  )}
                  {renderInput(
                    "insurancePolicy",
                    "manageEstablishment.new.fields.insurancePolicy"
                  )}
                  {renderDate(
                    "insuranceExpiry",
                    "manageEstablishment.new.fields.insuranceExpiry"
                  )}
                </div>
                <div className={styles.activityGrid}>
                  {[
                    ["travel", "activityTravel", "manageEstablishment.new.activities.travel"],
                    ["accommodation", "activityAccommodation", "manageEstablishment.new.activities.accommodation"],
                  ].map(([key, fieldName, label]) => (
                    <label key={key} className={`${styles.activityCard} ${styles.toggleField}`}>
                      <input
                        id={fieldName}
                        name={fieldName}
                        className={styles.toggleInput}
                        type="checkbox"
                        checked={activities.includes(key)}
                        onChange={() => toggleActivity(key)}
                      />
                      <span className={styles.toggleTrack} aria-hidden="true">
                        <span className={styles.toggleThumb} />
                      </span>
                      <span>{t(label)}</span>
                    </label>
                  ))}
                </div>
              </FormSection>
            </div>
          )}

          {activeStep === 1 && (
            <>
              <FormSection title={t("manageEstablishment.new.add.sections.governance")}>
                <div className={styles.gridTwo}>
                  {renderInput(
                    "firstName",
                    "manageEstablishment.new.fields.firstName",
                    "text",
                    { required: true },
                    "firstName"
                  )}
                  {renderInput(
                    "lastName",
                    "manageEstablishment.new.fields.lastName",
                    "text",
                    { required: true },
                    "lastName"
                  )}
                  <SelectMenu t={t}
                    name="identityType"
                    inputSize="medium"
                    options={[
                      { id: "CIN", value: "CIN", text: t("manageEstablishment.new.identityTypes.cin") },
                      { id: "PASSPORT", value: "PASSPORT", text: t("manageEstablishment.new.identityTypes.passport") },
                    ]}
                    value={form.identityType}
                    placeholder={`${t("manageEstablishment.new.fields.identityType")} *`}
                    errors={errors?.identityDocumentType}
                    onChange={updateSelect("identityType")}
                  />
                  {renderInput(
                    "identityNumber",
                    "manageEstablishment.new.fields.identityNumber",
                    "text",
                    { required: true },
                    "identityNumber"
                  )}
                  {renderDate(
                    "identityExpiry",
                    "manageEstablishment.new.fields.identityExpiry",
                    "identityExpiryDate",
                    true
                  )}
                  {renderInput(
                    "professionalEmail",
                    "manageEstablishment.new.fields.professionalEmail",
                    "email",
                    { required: true },
                    "email"
                  )}
                  <InternationalPhoneInput
                    name="phoneNumber"
                    className={styles.phoneControl}
                    value={phone.value}
                    onChange={phone.onChange}
                    placeholder={`${t("manageEstablishment.new.fields.phoneNumber")} *`}
                    inputSize="medium"
                    defaultCountry="MA"
                    validate={false}
                    errors={[
                      ...[phone.errors.indicative, phone.errors.number]
                        .filter(Boolean)
                        .map((err) => t(`phoneValidation.${err.code}`, err.message)),
                      ...(errors?.phoneNumber || []),
                    ]}
                  />
                  {renderInput(
                    "capitalShare",
                    "manageEstablishment.new.fields.capitalShare",
                    "number",
                    { required: true },
                    "ownershipPercentage"
                  )}
                  <SearchSelect t={t}
                    floatingLabel
                    inputSize="medium"
                    label={t("manageEstablishment.new.fields.nationality")}
                    name="nationality"
                    required
                    errors={errors?.nationality}
                    value={form.nationality}
                    options={nationalityOptions}
                    onChange={(code) => setForm((prev) => ({ ...prev, nationality: code || "" }))}
                  />
                </div>
              </FormSection>

              <FormSection title={t("manageEstablishment.new.add.sections.documents")}>
                <div className={styles.documentsCentered}>
                  <DocumentUploadField
                    label={t("manageEstablishment.new.fields.identityDocument")}
                    file={documents.identity}
                    onUpload={handleDocumentPick("identity")}
                    onRemove={() => removeDocument("identity")}
                  />
                </div>
              </FormSection>
            </>
          )}

          {activeStep === 2 && (
            <FormSection title={t("manageEstablishment.new.add.sections.finance")}>
              <div className={styles.gridOne}>
                {renderInput("bank", "manageEstablishment.new.fields.bank", "text", { required: true }, "bankName")}
                {renderInput(
                  "accountHolder",
                  "manageEstablishment.new.fields.accountHolder",
                  "text",
                  { required: true },
                  "accountHolder"
                )}
                {renderInput(
                  "rib",
                  "manageEstablishment.new.fields.rib",
                  "text",
                  { maxLength: 24, required: true },
                  "rib"
                )}
              </div>
            </FormSection>
          )}
        </div>

        <footer className={styles.footer}>
          <Button
            variant="secondary"
            label={t("manageEstablishment.new.actions.back")}
            onClick={() => setActiveStep((step) => step - 1)}
            disabled={activeStep === 0}
          />
          <Button
            primary
            label={
              activeStep === steps.length - 1
                ? t("manageEstablishment.new.actions.submit")
                : t("manageEstablishment.new.actions.continue")
            }
            onClick={nextStep}
            disabled={isSaving}
          />
        </footer>
      </div>
    </section>
  );
};

const FormSection = ({ title, children }) => (
  <section className={styles.section}>
    <Text as="h2" size="lg" weight="semibold" className={styles.sectionTitle}>
      {title}
    </Text>
    {children}
  </section>
);

export default AddEstablishementNew;
